import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { DigitalChannelTypes } from 'icreate/common/models/channel-config';
import { ChannelType, getDigitalTypesSet } from 'icreate/common/models/channel-type';
import { DisabledStatus } from 'icreate/common/models/editor-viewer-element';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-button-disabled-state',
    templateUrl: './button-disabled-state.component.html',
    styleUrls: ['./button-disabled-state.component.css'],
    standalone: false,
})
export class ButtonDisabledStateComponent {
    @Input() vesselId: string;
    @Input() disabledStatus: DisabledStatus;
    @Input() isDisabled: boolean;
    @ViewChild('channelSearchDialog') channelSearchDialog: ChannelSearchDialogComponent;

    disabledOptionIsOpen: boolean = false;

    disableChannel: Channel;
    isLoadingChannel: boolean = false;

    showingStatusOptions: boolean = false;

    statusOptions: string[];
    selectedStatus: string = undefined;

    allowedTypesSet: Set<ChannelType> = new Set(getDigitalTypesSet());

    constructor(
        private channelService: ChannelService,
        private dialog: MatDialog
    ) {}

    async ngOnChanges() {
        if (this.disabledStatus) {
            this.isLoadingChannel = true;
            const channel = await this.channelService.getByRowId(this.vesselId, this.disabledStatus.channelTag);
            if (channel) {
                this.disableChannel = channel;
                this.disabledOptionIsOpen = this.disabledStatus.enabled;
                const contactOn = channel.fcuData.digitalAlarmSetting.statusStringContactOn;
                const contactOff = channel.fcuData.digitalAlarmSetting.statusStringContactOff;
                this.selectedStatus = this.disabledStatus.status === 1 ? contactOn : contactOff;
                this.statusOptions = [contactOn, contactOff];
            }
            this.isLoadingChannel = false;
        }
    }

    updateToggle(event) {
        this.disabledOptionIsOpen = event.checked;
    }

    async openChannelSelector() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: this.disableChannel,
                allowedTypesSet: DigitalChannelTypes,
                setShouldDisableTypes: true,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.disableChannel = undefined;
            try {
                this.isLoadingChannel = true;
                const channel = await this.channelService.getByFcuChannelId(
                    this.vesselId,
                    channelSelected.fcuData.fcuChannelId
                );
                this.isLoadingChannel = false;
                if (channel) {
                    this.disableChannel = channel;
                    this.selectedStatus = undefined;
                    this.statusOptions = [
                        this.disableChannel.fcuData.digitalAlarmSetting.statusStringContactOn,
                        this.disableChannel.fcuData.digitalAlarmSetting.statusStringContactOff,
                    ];
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoadingChannel = false;
            }
        }
    }

    removeAssignedChannel() {
        this.disableChannel = undefined;
        this.selectedStatus = undefined;
    }

    get currentDisabledStatus(): DisabledStatus {
        if (!this.disableChannel || !this.selectedStatus) {
            return null;
        }
        const contactOn = this.disableChannel.fcuData.digitalAlarmSetting.statusStringContactOn;
        return {
            channelTag: this.disableChannel?.rowId.toString(),
            status: this.selectedStatus === contactOn ? 1 : 0,
            enabled: this.disabledOptionIsOpen,
        };
    }

    get hasMissingParameters() {
        if (this.disabledOptionIsOpen) {
            return !this.disableChannel || !this.selectedStatus;
        }
        return false;
    }
}
